import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { publicacao, type DiferencaPendente } from '../api/publicacao'
import { useSessao } from '../auth/useSessao'
import { Aviso } from './Aviso'
import './componentes.css'

/**
 * O aviso de que há configuração gravada e **não publicada** (demanda 123).
 *
 * ## Por que existe
 *
 * A 049 separou rascunho de publicado de propósito: **publicar é o ato de
 * conferência**, e é o que dá o `rollback`. O que faltou foi tornar a separação
 * visível.
 *
 * 🔴 Em 26/08 o Vicente gravou duas regras pelo portal — `prazo_fabricacao_dias`
 * para 0 e `tiragem_maxima_automatica` para 300 — e **nenhuma das duas valeu**
 * por onze horas. A tela confirmou a gravação, o comportamento não mudou, e não
 * havia como perceber sem comparar duas tabelas do banco.
 *
 * ⭐ **`gravar ≠ aplicar` na quarta ocorrência do projeto, e a primeira com o
 * cliente do outro lado.** Nas anteriores quem se enganou fomos nós, e dá para
 * chamar de descuido. Quando é o cliente, é a tela que está errada.
 *
 * ## O que este componente NÃO faz
 *
 * ⚠️ **Não publica sozinho.** Publicar ao gravar resolveria o sintoma e
 * destruiria a conferência: uma regra meio digitada passaria a valer no mesmo
 * segundo, em produção, com cliente do outro lado. O problema nunca foi a
 * publicação existir — foi ela ser invisível.
 *
 * ## E quem não pode publicar
 *
 * 🔴 **O aviso aparece igual**, com a orientação de a quem pedir, em vez de um
 * botão que falha. Um aviso visível só para quem já pode resolver não avisa
 * ninguém — e em setembro o portal fica com o cliente, onde nem todo mundo tem
 * `ParametroGerenciar`.
 */
export function PendenciasDePublicacao({ recarregar }: { recarregar?: number }) {
  const { t } = useTranslation()
  const { pode } = useSessao()

  const podePublicar = pode('ParametroGerenciar')

  const [itens, setItens] = useState<DiferencaPendente[] | null>(null)

  const carregar = useCallback(async () => {
    try {
      const p = await publicacao.pendencias()
      setItens(p.itens)
    } catch {
      // ⚠️ **Silêncio proposital.** Este aviso é um acréscimo à tela onde vive;
      // uma falha ao consultá-lo não pode virar um erro vermelho em cima do
      // trabalho que a pessoa está fazendo. Sem resposta, não se afirma nada.
      setItens(null)
    }
  }, [])

  useEffect(() => {
    void carregar()
  }, [carregar, recarregar])

  if (!itens || itens.length === 0) return null

  const mostrados = itens.slice(0, 8)
  const restantes = itens.length - mostrados.length

  return (
    <Aviso tom="alerta" titulo={t('pendencias.titulo')}>
      <p>
        {itens.length === 1
          ? t('pendencias.uma')
          : t('pendencias.varias', { total: itens.length })}{' '}
        {t('pendencias.explicacao')}
      </p>

      <table className="pendencias">
        <thead>
          <tr>
            <th>{t('pendencias.colunaItem')}</th>
            <th>{t('pendencias.colunaPublicado')}</th>
            <th>{t('pendencias.colunaVivo')}</th>
          </tr>
        </thead>
        <tbody>
          {mostrados.map((d) => (
            <tr key={d.chave}>
              <td>{d.rotulo}</td>
              {/* ⭐ Os dois valores lado a lado: é o critério 3 da ficha, e é o
                  que faltava para o Vicente enxergar que o 33 continuava lá. */}
              <td className="pendencias__valendo">
                {d.publicado ?? t('pendencias.ausente')}
              </td>
              <td className="pendencias__gravado">{d.vivo ?? t('pendencias.removido')}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {restantes > 0 && <p>{t('pendencias.verMais', { quantos: restantes })}</p>}

      {podePublicar ? (
        <p>
          <a href="/configuracao/publicacao">{t('pendencias.irParaPublicacao')}</a>
        </p>
      ) : (
        <p>{t('pendencias.semPermissao')}</p>
      )}
    </Aviso>
  )
}
